/**
 * PhotoAnnotator — full-screen canvas annotation tool for MIP photo reports.
 * Supports: draw circles/arrows (red), select, move, resize via handles, undo.
 */
import React, { useRef, useEffect, useState, useCallback } from "react";

const RED = "#FF0000";
let _nextId = 1;
const genId = () => `s${_nextId++}`;

// ── Metrics (screen→canvas scale) ────────────────────────────────────────────
function getMetrics(canvas) {
    if (!canvas) return { strokeW: 9, arrowHead: 34, handleR: 16, hitR: 26 };
    const rect = canvas.getBoundingClientRect();
    const scale = canvas.width / (rect.width || 1);
    return {
        strokeW: 9 * scale,       // visible stroke in screen px = 9
        arrowHead: 34 * scale,    // arrowhead size in screen px = 34
        handleR: 14 * scale,      // handle circle radius = 14 screen px
        hitR: 28 * scale,         // hit-test tolerance = 28 screen px
    };
}

// ── Handle positions per shape ────────────────────────────────────────────────
function getHandles(shape) {
    const { type, x1, y1, x2, y2 } = shape;
    if (type === "circle") {
        return [
            { id: "tl", x: x1, y: y1 },
            { id: "tr", x: x2, y: y1 },
            { id: "br", x: x2, y: y2 },
            { id: "bl", x: x1, y: y2 },
        ];
    }
    return [
        { id: "start", x: x1, y: y1 },
        { id: "end",   x: x2, y: y2 },
    ];
}

// Apply a handle drag delta to a shape
function applyHandle(shape, handleId, dx, dy) {
    const s = { ...shape };
    switch (handleId) {
        case "tl":    case "start": s.x1 += dx; s.y1 += dy; break;
        case "tr":                  s.x2 += dx; s.y1 += dy; break;
        case "br":    case "end":   s.x2 += dx; s.y2 += dy; break;
        case "bl":                  s.x1 += dx; s.y2 += dy; break;
        case "move":                s.x1 += dx; s.y1 += dy; s.x2 += dx; s.y2 += dy; break;
        default: break;
    }
    return s;
}

// ── Hit testing ──────────────────────────────────────────────────────────────
function hitHandle(shape, pos, hitR) {
    for (const h of getHandles(shape)) {
        if (Math.hypot(pos.x - h.x, pos.y - h.y) <= hitR) return h.id;
    }
    return null;
}

function hitShape(shape, pos, hitR) {
    const { type, x1, y1, x2, y2 } = shape;
    if (type === "circle") {
        const rx = Math.abs(x2 - x1) / 2, ry = Math.abs(y2 - y1) / 2;
        if (rx < 2 || ry < 2) return false;
        const cx = (x1 + x2) / 2, cy = (y1 + y2) / 2;
        const nx = (pos.x - cx) / rx, ny = (pos.y - cy) / ry;
        const d = Math.sqrt(nx * nx + ny * ny);
        // Hit inside ellipse OR near its border
        return d <= 1.0 + hitR / Math.min(rx, ry);
    }
    // Arrow: distance from point to line segment
    const dx = x2 - x1, dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return false;
    const t = Math.max(0, Math.min(1, ((pos.x - x1) * dx + (pos.y - y1) * dy) / len2));
    return Math.hypot(pos.x - (x1 + t * dx), pos.y - (y1 + t * dy)) <= hitR;
}

// ── Drawing ──────────────────────────────────────────────────────────────────
function drawShape(ctx, shape, selected, m) {
    const { strokeW, arrowHead, handleR } = m;
    const { type, x1, y1, x2, y2 } = shape;
    const dx = x2 - x1, dy = y2 - y1;

    ctx.save();
    ctx.strokeStyle = RED;
    ctx.fillStyle = RED;
    ctx.lineWidth = strokeW;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (type === "circle") {
        const rx = Math.abs(dx) / 2, ry = Math.abs(dy) / 2;
        if (rx < 3 || ry < 3) { ctx.restore(); return; }
        ctx.beginPath();
        ctx.ellipse((x1 + x2) / 2, (y1 + y2) / 2, rx, ry, 0, 0, 2 * Math.PI);
        ctx.stroke();
    } else if (type === "arrow") {
        if (Math.hypot(dx, dy) < 6) { ctx.restore(); return; }
        const angle = Math.atan2(dy, dx);
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
        // Filled arrowhead
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - arrowHead * Math.cos(angle - Math.PI / 7), y2 - arrowHead * Math.sin(angle - Math.PI / 7));
        ctx.lineTo(x2 - arrowHead * Math.cos(angle + Math.PI / 7), y2 - arrowHead * Math.sin(angle + Math.PI / 7));
        ctx.closePath();
        ctx.fill();
    }

    // Handles for selected shape
    if (selected) {
        for (const h of getHandles(shape)) {
            ctx.beginPath();
            ctx.arc(h.x, h.y, handleR, 0, 2 * Math.PI);
            ctx.fillStyle = "white";
            ctx.fill();
            ctx.strokeStyle = RED;
            ctx.lineWidth = strokeW * 0.55;
            ctx.stroke();
        }
    }

    ctx.restore();
}

// ── Component ─────────────────────────────────────────────────────────────────
export function PhotoAnnotator({ imageDataUri, onConfirm, onCancel }) {
    const canvasRef    = useRef(null);
    const imgRef       = useRef(null);
    const shapesRef    = useRef([]);
    const selectedRef  = useRef(null);   // id of selected shape
    const toolRef      = useRef("circle");
    const iaRef        = useRef(null);   // current interaction

    const [tool,     setTool]     = useState("circle");
    const [version,  setVersion]  = useState(0);   // triggers re-render/redraw
    const [imgReady, setImgReady] = useState(false);

    const bump = useCallback(() => setVersion(v => v + 1), []);

    // Load image
    useEffect(() => {
        const img = new Image();
        img.onload = () => { imgRef.current = img; setImgReady(true); };
        img.src = imageDataUri;
    }, [imageDataUri]);

    // Size canvas to image natural resolution
    useEffect(() => {
        if (!imgReady) return;
        const cv = canvasRef.current, img = imgRef.current;
        cv.width  = img.naturalWidth;
        cv.height = img.naturalHeight;
        redraw();
    }, [imgReady]); // eslint-disable-line

    const redraw = useCallback((preview = null) => {
        const cv = canvasRef.current, img = imgRef.current;
        if (!cv || !img) return;
        const ctx = cv.getContext("2d");
        const m = getMetrics(cv);
        ctx.clearRect(0, 0, cv.width, cv.height);
        ctx.drawImage(img, 0, 0, cv.width, cv.height);
        const all = [...shapesRef.current, ...(preview ? [preview] : [])];
        all.forEach(s => drawShape(ctx, s, s.id === selectedRef.current, m));
    }, []);

    // Sync redraw on version bump
    useEffect(() => { if (imgReady) redraw(); }, [version, imgReady, redraw]);

    // Keep toolRef synced
    useEffect(() => { toolRef.current = tool; }, [tool]);

    const toCanvas = (clientX, clientY) => {
        const cv = canvasRef.current;
        const r  = cv.getBoundingClientRect();
        return {
            x: (clientX - r.left) * (cv.width  / r.width),
            y: (clientY - r.top)  * (cv.height / r.height),
        };
    };

    // ── Event listeners ──────────────────────────────────────────────────────
    useEffect(() => {
        const cv = canvasRef.current;
        if (!cv || !imgReady) return;

        const onStart = (e) => {
            e.preventDefault();
            const src = e.touches ? e.touches[0] : e;
            const pos = toCanvas(src.clientX, src.clientY);
            const { hitR } = getMetrics(cv);

            // 1. Handles of currently selected shape (highest priority)
            const selShape = shapesRef.current.find(s => s.id === selectedRef.current);
            if (selShape) {
                const hid = hitHandle(selShape, pos, hitR);
                if (hid) {
                    iaRef.current = { mode: "resize", shapeId: selShape.id, handleId: hid, sx: pos.x, sy: pos.y, orig: { ...selShape } };
                    return;
                }
                if (hitShape(selShape, pos, hitR)) {
                    iaRef.current = { mode: "move", shapeId: selShape.id, sx: pos.x, sy: pos.y, orig: { ...selShape } };
                    return;
                }
            }

            // 2. Any other shape (topmost = last in array)
            for (let i = shapesRef.current.length - 1; i >= 0; i--) {
                const s = shapesRef.current[i];
                const hid = hitHandle(s, pos, hitR);
                if (hid) {
                    selectedRef.current = s.id;
                    iaRef.current = { mode: "resize", shapeId: s.id, handleId: hid, sx: pos.x, sy: pos.y, orig: { ...s } };
                    bump();
                    return;
                }
                if (hitShape(s, pos, hitR)) {
                    selectedRef.current = s.id;
                    iaRef.current = { mode: "move", shapeId: s.id, sx: pos.x, sy: pos.y, orig: { ...s } };
                    bump();
                    return;
                }
            }

            // 3. Empty area → draw new shape
            selectedRef.current = null;
            const nid = genId();
            iaRef.current = { mode: "draw", newId: nid, sx: pos.x, sy: pos.y };
        };

        const onMove = (e) => {
            e.preventDefault();
            const ia = iaRef.current;
            if (!ia) return;
            const src = e.touches ? e.touches[0] : e;
            const pos = toCanvas(src.clientX, src.clientY);

            if (ia.mode === "draw") {
                redraw({ id: ia.newId, type: toolRef.current, x1: ia.sx, y1: ia.sy, x2: pos.x, y2: pos.y });
                return;
            }
            const dx = pos.x - ia.sx, dy = pos.y - ia.sy;
            const hid = ia.mode === "move" ? "move" : ia.handleId;
            const updated = applyHandle(ia.orig, hid, dx, dy);
            shapesRef.current = shapesRef.current.map(s => s.id === ia.shapeId ? updated : s);
            redraw();
        };

        const onEnd = (e) => {
            e.preventDefault();
            const ia = iaRef.current;
            if (!ia) return;

            if (ia.mode === "draw") {
                const src = e.changedTouches ? e.changedTouches[0] : e;
                const pos = toCanvas(src.clientX, src.clientY);
                if (Math.hypot(pos.x - ia.sx, pos.y - ia.sy) > 8) {
                    const ns = { id: ia.newId, type: toolRef.current, x1: ia.sx, y1: ia.sy, x2: pos.x, y2: pos.y };
                    shapesRef.current = [...shapesRef.current, ns];
                    selectedRef.current = ns.id;
                }
            }

            iaRef.current = null;
            bump();
        };

        cv.addEventListener("mousedown",  onStart);
        cv.addEventListener("mousemove",  onMove);
        cv.addEventListener("mouseup",    onEnd);
        cv.addEventListener("mouseleave", () => { iaRef.current = null; });
        cv.addEventListener("touchstart", onStart, { passive: false });
        cv.addEventListener("touchmove",  onMove,  { passive: false });
        cv.addEventListener("touchend",   onEnd,   { passive: false });

        return () => {
            cv.removeEventListener("mousedown",  onStart);
            cv.removeEventListener("mousemove",  onMove);
            cv.removeEventListener("mouseup",    onEnd);
            cv.removeEventListener("mouseleave", () => {});
            cv.removeEventListener("touchstart", onStart);
            cv.removeEventListener("touchmove",  onMove);
            cv.removeEventListener("touchend",   onEnd);
        };
    }, [imgReady, redraw, bump]); // eslint-disable-line

    const handleUndo = () => {
        if (!shapesRef.current.length) return;
        const last = shapesRef.current[shapesRef.current.length - 1];
        shapesRef.current = shapesRef.current.slice(0, -1);
        if (selectedRef.current === last.id) selectedRef.current = null;
        bump();
    };

    // Export: deselect first so handles aren't in the final image
    const handleConfirm = () => {
        selectedRef.current = null;
        redraw();
        onConfirm(canvasRef.current.toDataURL("image/jpeg", 0.88));
    };

    const hasShapes = version >= 0 && shapesRef.current.length > 0; // reactive via version

    return (
        <div className="fixed inset-0 z-[100] bg-black flex flex-col" style={{ touchAction: "none" }} data-testid="photo-annotator">

            {/* ── Toolbar ── */}
            <div className="flex items-center gap-2 px-3 py-3 bg-zinc-900 border-b border-zinc-800 shrink-0">
                <p className="text-white text-sm font-bold flex-1 leading-none">Anotar foto</p>

                <button onClick={() => setTool("circle")} data-testid="annotate-circle-btn"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                        tool === "circle" ? "bg-red-600 text-white" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                    }`}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="7" cy="7" r="5.5"/>
                    </svg>
                    Círculo
                </button>

                <button onClick={() => setTool("arrow")} data-testid="annotate-arrow-btn"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                        tool === "arrow" ? "bg-red-600 text-white" : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                    }`}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="2" y1="12" x2="12" y2="2"/>
                        <polyline points="6,2 12,2 12,8"/>
                    </svg>
                    Flecha
                </button>

                <button onClick={handleUndo} disabled={!hasShapes} data-testid="annotate-undo-btn"
                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:opacity-30 transition-colors">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M1.5 5.5 A5 5 0 1 1 3 2.5" />
                        <polyline points="1.5,2 1.5,5.5 5,5.5"/>
                    </svg>
                    Deshacer
                </button>
            </div>

            {/* ── Hint text ── */}
            <div className="bg-zinc-900 px-3 pb-2 shrink-0">
                <p className="text-center text-[11px] text-zinc-400">
                    {tool === "circle"
                        ? "Arrastra para dibujar un círculo · Toca una forma existente para moverla o cambiar su tamaño"
                        : "Arrastra para dibujar una flecha · Toca una forma existente para moverla o cambiar su tamaño"}
                </p>
            </div>

            {/* ── Canvas area ── */}
            <div className="flex-1 flex items-center justify-center bg-black overflow-hidden p-2">
                {!imgReady ? (
                    <div className="flex flex-col items-center gap-2">
                        <span className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-white text-xs">Cargando imagen...</span>
                    </div>
                ) : (
                    <canvas ref={canvasRef} data-testid="annotate-canvas"
                        style={{ maxWidth: "100%", maxHeight: "100%", display: "block",
                                 cursor: "crosshair", touchAction: "none", userSelect: "none" }}
                    />
                )}
            </div>

            {/* ── Bottom actions ── */}
            <div className="flex gap-3 px-4 py-4 bg-zinc-900 border-t border-zinc-800 shrink-0">
                <button onClick={onCancel} data-testid="annotate-cancel-btn"
                    className="flex-1 py-3.5 rounded-xl text-sm font-semibold text-zinc-300 border border-zinc-600 bg-zinc-800 active:bg-zinc-700 transition-colors">
                    Cancelar
                </button>
                <button onClick={handleConfirm} disabled={!imgReady} data-testid="annotate-confirm-btn"
                    className="flex-1 py-3.5 rounded-xl text-sm font-bold bg-red-600 text-white active:bg-red-700 disabled:opacity-50 transition-colors">
                    Usar foto
                </button>
            </div>
        </div>
    );
}
